/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import * as XLSX from "xlsx";
import { useDispatch, useSelector } from "react-redux";
import { globalAdminReportDetails } from "../../Services/redux/global-admin-users/dashboard/GlobalAdminDashboardActions";

const GlobalAdminDownloadReportPopup = ({
  open,
  handleClose,
  currentFilters,
}) => {
  const dispatch = useDispatch();

  const defaultSelectedFields = [
    "appName",
    "category",
    "apiLevel",
    "lastModifiedDate",
  ];

  const fieldOptions = [
    { key: "appName", value: "App Name" },
    { key: "category", value: "Category" },
    { key: "countries", value: "Countries" },
    { key: "makes", value: "Makes" },
    { key: "languages", value: "Languages" },
    { key: "ecuTypes", value: "ECU Types" },
    { key: "latestVersion", value: "Latest Version" },
    { key: "apiLevel", value: "API Level" },
    { key: "shortDescription", value: "Short Description" },
    { key: "longDescription", value: "Long Description" },
    { key: "lastModifiedDate", value: "Last Modified Date" },
    { key: "isPaymentEnabled", value: "Is Payment Enabled" },
    { key: "cpuTypes", value: "CPU Type" },
    { key: "appPricing", value: "App Pricing" },
    { key: "packageName", value: "Package Name" },
  ];

  const fieldLabelMap = Object.fromEntries(fieldOptions.map((f) => [f.key, f.value]));

  const [selectedFields, setSelectedFields] = useState(defaultSelectedFields);
  const [downloadClicked, setDownloadClicked] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const fullData = useSelector(
    (state) => state.globalAdminDashboard.globalAdminDownloadData
  );

  const arrayFields = new Set([
    "countries",
    "ecuTypes",
    "languages",
    "makes",
    "cpuTypes",
  ]);

  const handleFieldChange = (event) => {
    setSelectedFields(event.target.value);
  };

  const getArrayFieldValue = (fieldValue) => {
    if (Array.isArray(fieldValue)) {
      return fieldValue.join(",");
    }
    return fieldValue || "";
  };

  useEffect(() => {
    if (fullData && downloadClicked) {
      const updatedDataList = fullData || [];
      const processedData = updatedDataList.map((fileObj) => {
        let ob = {};
        selectedFields.forEach((field) => {
          const fieldLabel = fieldLabelMap[field];

          if (arrayFields.has(field)) {
            ob[fieldLabel] = getArrayFieldValue(fileObj[field]);
          } else if (field === "lastModifiedDate") {
            ob["Last Modified Date"] = fileObj[field]
              ? new Date(fileObj[field]).toLocaleDateString()
              : "";
          } else if (field === "isPaymentEnabled") {
            ob["Is Payment Enabled"] = Boolean(
              fileObj.isPremiumApp && fileObj.isInAppPurchase
            );
          } else {
            ob[fieldLabel] = fileObj[field] || "";
          }
        });
        return ob;
      });
      const worksheet = XLSX.utils.json_to_sheet(processedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

      // Create filename with current date and time
      const currentDate = new Date();
      const dateString =
        currentDate.getFullYear() +
        String(currentDate.getMonth() + 1).padStart(2, "0") +
        String(currentDate.getDate()).padStart(2, "0");
      const timeString =
        String(currentDate.getHours()).padStart(2, "0") +
        String(currentDate.getMinutes()).padStart(2, "0") +
        String(currentDate.getSeconds()).padStart(2, "0");
      const reportFileName = `report_${dateString}_${timeString}.xlsx`;

      XLSX.writeFile(workbook, reportFileName);
      setSelectedFields(defaultSelectedFields);
      setDownloadClicked(false);
      setIsDownloading(false);
      handleClose();
    }
  }, [fullData, downloadClicked]);

  const handleDownload = async () => {
    setDownloadClicked(true);
    setIsDownloading(true);
    try {
      // Use the filters from parent component
      const requestBody = {
        ...currentFilters,
      };

      //Fetch latest data with all records before downloading
      dispatch(globalAdminReportDetails(requestBody));

      // Use the Redux state from top-level selector (fullData)
    } catch (error) {
      console.error("Error downloading report:", error);
      setIsDownloading(false);
      setDownloadClicked(false);
    }
  };
  const handleCancel = () => {
    setSelectedFields(defaultSelectedFields);
    handleClose();
  };

  const renderButton = (label, onClick, isCancel = false) => (
    <Button
      onClick={onClick}
      variant={isCancel ? "text" : "contained"}
      sx={
        isCancel
          ? {
              textTransform: "none",
              background: "#1214151A",
              color: "#2A2C2D",
              padding: "8px 16px",
            }
          : {
              textTransform: "none",
              fontSize: "0.9rem",
              padding: "8px 16px",
            }
      }
    >
      {label}
    </Button>
  );

  return (
    <>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.modal + 1,
        }}
        open={isDownloading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Dialog
        open={open}
        onClose={handleClose}
        sx={{
          "& .MuiDialog-paper": {
            padding: "20px",
            minWidth: "400px",
            borderRadius: "8px",
            boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
          },
        }}
      >
        <DialogTitle
          sx={{ fontSize: "1.2rem", fontWeight: "bold", padding: "10px 24px" }}
        >
          Download Report
        </DialogTitle>
        <DialogContent sx={{ paddingTop: "5px !important" }}>
          <FormControl fullWidth>
            <InputLabel
              id="select-field-label"
              sx={{
                fontSize: "0.9rem",
                fontWeight: "bold",
                color: "black",
                backgroundColor: "white",
                px: 0.5,
                transform: "translate(14px, -6px)",
                top: "-3px",
              }}
            >
              Select Field Attribute
            </InputLabel>
            <Select
              multiple
              value={selectedFields}
              onChange={handleFieldChange}
              displayEmpty
              MenuProps={{
                disablePortal: true,
                PaperProps: {
                  style: {
                    top: "400px !important",
                    maxHeight: 300,
                    overflow: "auto",
                  },
                },
              }}
              renderValue={(selected) => {
                const selectedLabels = selected.map((key) => fieldLabelMap[key] || key);
                if (selectedLabels.length > 4) {
                  return `${selectedLabels.slice(0, 4).join(", ")} ...`;
                }
                return selectedLabels.join(", ");
              }}
              sx={{
                fontSize: "14px",
                borderRadius: "6px",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  minHeight: "40px",
                },
              }}
            >
              {fieldOptions.map((field) => (
                <MenuItem key={field.key} value={field.key}>
                  <Checkbox checked={selectedFields.includes(field.key)} />
                  <ListItemText primary={field.value} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          {renderButton("Cancel", handleCancel, true)}
          {renderButton("Download", handleDownload, false)}
        </DialogActions>
      </Dialog>
    </>
  );
};

GlobalAdminDownloadReportPopup.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  currentFilters: PropTypes.object.isRequired,
};

export default GlobalAdminDownloadReportPopup;
